import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Briefcase, Loader2, Check, X, Eye, ExternalLink, Bot,
  Pencil, ListChecks, Filter, Search,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { WorkModelBadge } from "@/components/jobs/WorkModelBadge";
import { DeadlineBadge } from "@/components/jobs/DeadlineBadge";
import { SourceBadge } from "@/components/jobs/SourceBadge";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SKILLS_CATALOG } from "@/lib/skills";
import { logAudit } from "@/lib/auditLog";
import { useAdminOrgContext } from "@/hooks/useAdminOrgContext";
import {
  isCrawlerJob as checkCrawler,
  getCompanyName,
  getSourceUrl,
  getCrawlerScore,
} from "@/lib/jobUtils";
import { JOB_STATUS_LABELS } from "@/lib/constants";
import { JobQualityBadge } from "@/components/admin/JobQualityBadge";
import { getJobQuality, type QualityTier } from "@/lib/jobQuality";
import { useJobLocations } from "@/hooks/useJobLocations";
import { CityCombobox } from "@/components/admin/CityCombobox";

const statusLabels = JOB_STATUS_LABELS;
const CRAWLER_ORG_ID = "00000000-0000-0000-0000-000000000001";
const PAGE_SIZE_OPTIONS = [20, 50, 100, 500] as const;

const MATCH_RANK_LABELS: Record<number, string> = {
  1: "título",
  2: "empresa",
  3: "resumo",
  4: "local",
  5: "skills",
  6: "descrição",
};

type SourceFilter = "all" | "manual" | "crawler" | "pending_crawler";
type StatusFilter = "em_revisao";
type QualityFilter = "all" | QualityTier;
type SortOption = "recent" | "oldest" | "quality_asc" | "quality_desc" | "crawler_recent";

function qualityLevel(job: any): "low" | "medium" | "high" {
  const q = getJobQuality(job);
  if (q.tier === "alta") return "high";
  if (q.tier === "media") return "medium";
  return "low";
}

function CompletenessBar({ job }: { job: any }) {
  const q = getJobQuality(job);
  const pct = Math.round((q.filled / q.total) * 100);
  const color = q.tier === "alta" ? "bg-green-500" : q.tier === "media" ? "bg-yellow-500" : "bg-destructive";
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
        <span className="flex items-center gap-1"><ListChecks className="w-3 h-3" /> {q.filled}/{q.total} campos</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SkillBadgeList({ skills }: { skills: any }) {
  if (!Array.isArray(skills) || skills.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {skills.map((s: string) => {
        const label = SKILLS_CATALOG.find((sk) => sk.id === s)?.label || s;
        return <Badge key={s} variant="secondary" className="text-xs">{label}</Badge>;
      })}
    </div>
  );
}

export default function AdminVagas() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const { orgId, orgs } = useAdminOrgContext();
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [statusFilter] = useState<StatusFilter>("em_revisao");
  const [orgFilter, setOrgFilter] = useState<string>("all");
  const [qualityFilter, setQualityFilter] = useState<QualityFilter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDiscard, setConfirmDiscard] = useState<{ type: "single"; id: string; status: string } | { type: "batch"; ids: string[]; status: string } | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("recent");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string | null>(null);

  const { states: availableStates, cities: availableCities } = useJobLocations({
    statusIn: ["em_revisao"],
    selectedState: stateFilter,
  });

  // --- Mutual exclusion handlers: search vs geo filters ---
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.trim() && (stateFilter || cityFilter)) {
      setStateFilter(null);
      setCityFilter(null);
      toast({ title: "Filtros de localidade removidos", description: "A busca pesquisa em todos os campos." });
    }
  };
  const handleStateChange = (value: string) => {
    setStateFilter(value === "all" ? null : value);
    setCityFilter(null);
    if (searchTerm.trim()) {
      setSearchTerm("");
      setDebouncedSearch("");
      toast({ title: "Busca removida", description: "Filtros de localidade aplicados." });
    }
    setPage(0);
  };
  const handleCityChange = (value: string | null) => {
    setCityFilter(value);
    if (searchTerm.trim()) {
      setSearchTerm("");
      setDebouncedSearch("");
      toast({ title: "Busca removida", description: "Filtros de localidade aplicados." });
    }
    setPage(0);
  };

  // Debounce search — 350ms
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 350);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchTerm]);

  // Clear selection on filter/page changes
  useEffect(() => {
    setSelected(new Set());
  }, [page, statusFilter, sourceFilter, orgFilter, qualityFilter, debouncedSearch, stateFilter, cityFilter]);

  const isSearching = debouncedSearch.trim().length > 0;
  const isQualityFiltering = qualityFilter !== "all";

  // Determine sort column for server query
  const orderAscending = sortBy === "oldest";

  const { data: queryResult, isLoading, isFetching } = useQuery({
    queryKey: ["admin_review_jobs", statusFilter, sourceFilter, orgFilter, debouncedSearch, sortBy, page, pageSize, stateFilter, cityFilter, qualityFilter],
    queryFn: async () => {
      // Full-text search via RPC
      if (isSearching) {
        const { data, error } = await (supabase.rpc as any)("search_review_jobs", {
          search_term: debouncedSearch.trim(),
          status_filter: statusFilter,
          org_filter: orgFilter === "all" ? null : orgFilter,
          source_filter: sourceFilter,
          result_limit: isQualityFiltering ? 500 : pageSize,
          result_offset: isQualityFiltering ? 0 : page * pageSize,
        });
        if (error) throw error;
        const rows = (data ?? []) as any[];
        const totalCount = rows.length > 0 ? Number(rows[0].total_count) : 0;
        return {
          jobs: rows.map((r: any) => ({
            id: r.id,
            title: r.title,
            employment_type: r.employment_type,
            work_model: r.work_model,
            status: r.status,
            created_at: r.created_at,
            org_id: r.org_id,
            description: r.description,
            description_summary: r.description_summary,
            location_city: r.location_city,
            location_state: r.location_state,
            stipend_min: r.stipend_min,
            deadline_date: r.deadline_date,
            responsibilities: r.responsibilities,
            requirements_must: r.requirements_must,
            required_skills: r.required_skills,
            nice_skills: r.nice_skills,
            skills_tags: r.skills_tags,
            published_at: r.published_at,
            benefits: r.benefits,
            process_steps: r.process_steps,
            requirements_nice: r.requirements_nice,
            stipend_max: r.stipend_max,
            partner_orgs: {
              name: r.org_name,
              is_auto_created: r.org_is_auto_created,
              source_origin: r.org_source_origin,
              logo_url: r.org_logo_url,
              industry: r.org_industry,
            },
            crawler_metadata: r.crawler_source_name ? [{
              company_name: r.crawler_company_name,
              source_url: r.crawler_source_url,
              source_name: r.crawler_source_name,
              source_type: r.crawler_source_type,
              external_apply_url: r.crawler_external_apply_url,
              crawler_score: r.crawler_score,
            }] : [],
            _match_rank: r.match_rank,
          })),
          totalCount,
        };
      }

      // Regular paginated query
      const fetchLimit = isQualityFiltering ? 500 : pageSize;
      const fetchOffset = isQualityFiltering ? 0 : page * pageSize;
      let query = supabase
        .from("job_posts")
        .select("*, partner_orgs(name, is_auto_created, source_origin, logo_url, industry), crawler_metadata(source_url, source_name, source_type, external_apply_url, company_name, crawler_score)", { count: "exact" })
        .order("created_at", { ascending: orderAscending })
        .range(fetchOffset, fetchOffset + fetchLimit - 1);

      // Always only em_revisao for this page
      query = query.eq("status", "em_revisao");

      // Org filter
      if (orgFilter !== "all") {
        query = query.eq("org_id", orgFilter);
      }

      // Source filter (crawler/manual)
      if (sourceFilter === "crawler") {
        query = query.eq("org_id", CRAWLER_ORG_ID);
      } else if (sourceFilter === "manual") {
        query = query.neq("org_id", CRAWLER_ORG_ID);
      } else if (sourceFilter === "pending_crawler") {
        query = query.eq("org_id", CRAWLER_ORG_ID).eq("status", "em_revisao");
      }
      if (stateFilter) query = query.eq("location_state", stateFilter);
      if (cityFilter) query = query.eq("location_city", cityFilter);

      const { data, error, count } = await query;
      if (error) throw error;
      return { jobs: data ?? [], totalCount: count ?? 0 };
    },
    placeholderData: (prev) => prev,
  });

  // Hybrid pagination: quality filter applied client-side
  const { jobs, totalCount, totalPages } = useMemo(() => {
    const raw = queryResult?.jobs ?? [];
    const serverTotal = queryResult?.totalCount ?? 0;
    if (!isQualityFiltering) {
      return { jobs: raw, totalCount: serverTotal, totalPages: Math.max(1, Math.ceil(serverTotal / pageSize)) };
    }
    const qualityFiltered = raw.filter((j: any) => getJobQuality(j).tier === qualityFilter);
    const sliced = qualityFiltered.slice(page * pageSize, (page + 1) * pageSize);
    return {
      jobs: sliced,
      totalCount: qualityFiltered.length,
      totalPages: Math.max(1, Math.ceil(qualityFiltered.length / pageSize)),
    };
  }, [queryResult, qualityFilter, isQualityFiltering, page, pageSize]);

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const update: any = { status };
      if (status === "publicada") update.published_at = new Date().toISOString();
      const { error } = await supabase.from("job_posts").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { id, status }) => {
      queryClient.invalidateQueries({ queryKey: ["admin_review_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin_active_jobs"] });
      setSelectedJob(null);
      toast({ title: "Status da vaga atualizado" });
      if (orgId) {
        const action = status === "publicada" ? "approve_job" : status === "descartada" ? "discard_job" : status === "rascunho" ? "reject_job" : "publish_job";
        logAudit({ orgId, action: action as any, entityType: "job_post", entityId: id });
      }
    },
    onError: (err) => {
      toast({ title: "Erro ao atualizar vaga", description: (err as Error).message, variant: "destructive" });
    },
  });

  const batchUpdate = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      const update: any = { status };
      if (status === "publicada") update.published_at = new Date().toISOString();
      // Chunk to avoid Supabase/PostgREST URL length limits
      const CHUNK = 200;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const { error } = await supabase.from("job_posts").update(update).in("id", chunk);
        if (error) throw error;
      }
    },
    onSuccess: (_, { ids, status }) => {
      queryClient.invalidateQueries({ queryKey: ["admin_review_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin_active_jobs"] });
      setSelected(new Set());
      toast({ title: `${ids.length} vaga(s) ${status === "publicada" ? "publicadas" : status === "pending_review" ? "devolvidas p/ enriquecimento" : "descartadas"}` });
    },
    onError: (err) => {
      toast({ title: "Erro ao atualizar vagas", description: (err as Error).message, variant: "destructive" });
    },
  });

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllPage = useCallback((ids: string[]) => {
    setSelected((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...ids]);
    });
  }, []);

  const selectAllFiltered = useCallback(async () => {
    const MAX = 500;
    const offset = page * pageSize;
    const buildQuery = () => {
      let q = supabase.from("job_posts").select("id").eq("status", "em_revisao");
      if (orgFilter !== "all") q = q.eq("org_id", orgFilter);
      if (sourceFilter === "crawler") q = q.eq("org_id", CRAWLER_ORG_ID);
      else if (sourceFilter === "manual") q = q.neq("org_id", CRAWLER_ORG_ID);
      else if (sourceFilter === "pending_crawler") q = q.eq("org_id", CRAWLER_ORG_ID);
      return q;
    };
    // Fetch from current page offset first
    const { data: fromCurrent } = await buildQuery().range(offset, offset + MAX - 1);
    let ids = (fromCurrent ?? []).map((d) => d.id);
    // If not enough and we started after 0, wrap around to fill from beginning
    if (ids.length < MAX && offset > 0) {
      const remaining = MAX - ids.length;
      const { data: fromStart } = await buildQuery().range(0, remaining - 1);
      const currentSet = new Set(ids);
      const extra = (fromStart ?? []).map((d) => d.id).filter((id) => !currentSet.has(id));
      ids = [...ids, ...extra].slice(0, MAX);
    }
    setSelected((prev) => {
      if (ids.every((id) => prev.has(id))) return new Set();
      return new Set(ids);
    });
  }, [statusFilter, orgFilter, sourceFilter, page, pageSize]);

  // Pre-compute isCrawler for all jobs in current page
  const crawlerMap = useMemo(
    () => new Map(jobs.map((j) => [j.id, checkCrawler(j)])),
    [jobs],
  );
  const isCrawler = useCallback((job: any) => crawlerMap.get(job.id) ?? false, [crawlerMap]);

  // Client-side sort filtering (quality already handled by hybrid pagination)
  const filteredJobs = useMemo(() => {
    let filtered = jobs;

    // Client-side sort for quality and crawler_recent (server handles date sort)
    if (sortBy === "quality_asc" || sortBy === "quality_desc" || sortBy === "crawler_recent") {
      filtered = [...filtered].sort((a, b) => {
        if (sortBy === "quality_asc") return getJobQuality(a).filled - getJobQuality(b).filled;
        if (sortBy === "quality_desc") return getJobQuality(b).filled - getJobQuality(a).filled;
        // crawler_recent
        const aCrawler = (crawlerMap.get(a.id) ? 0 : 1);
        const bCrawler = (crawlerMap.get(b.id) ? 0 : 1);
        if (aCrawler !== bCrawler) return aCrawler - bCrawler;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    }

    return filtered;
  }, [jobs, sortBy, crawlerMap]);

  // Fetch org list for org filter dropdown
  const { data: jobOrgs } = useQuery({
    queryKey: ["admin_job_orgs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_orgs")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return (data ?? []).map((o) => [o.id, o.name] as [string, string]);
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return <div className="p-8 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  // All jobs on this page are em_revisao
  const reviewJobs = filteredJobs;

  const pageIds = reviewJobs.map((j) => j.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const filterButtons: { key: SourceFilter; label: string }[] = [
    { key: "all", label: "Todas" },
    { key: "manual", label: "Manual" },
    { key: "crawler", label: "Crawler" },
    { key: "pending_crawler", label: "Pendentes Crawler" },
  ];

  // No status filter options needed - this page is always em_revisao

  return (
    <div className="p-8 max-w-6xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-2xl font-bold text-foreground">Revisão de Vagas</h1>
        <p className="text-muted-foreground text-sm mt-1 flex items-center gap-2">
          Aprove ou descarte vagas para publicação · {totalCount} aguardando revisão
          {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        </p>
      </motion.div>

      {/* Source filters + Search */}
      <div className="flex gap-2 mt-4 flex-wrap items-center">
        {filterButtons.map((f) => (
          <button
            key={f.key}
            onClick={() => { setSourceFilter(f.key); setPage(0); }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              sourceFilter === f.key
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="relative ml-auto w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por título, empresa, cidade, skills..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-8 pr-7 h-8 text-xs"
          />
          {searchTerm && (
            <button
              onClick={() => { setSearchTerm(""); setDebouncedSearch(""); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Advanced filters */}
      <div className="flex gap-3 mt-3 flex-wrap items-center">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Filtros:
        </div>
        <Select value={orgFilter} onValueChange={(v) => { setOrgFilter(v); setPage(0); }}>
          <SelectTrigger className="w-52 h-8 text-xs">
            <SelectValue placeholder="Organização" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as organizações</SelectItem>
            {(jobOrgs ?? []).map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* No status filter dropdown - this page is always em_revisao */}
        <Select value={qualityFilter} onValueChange={(v) => { setQualityFilter(v as QualityFilter); setPage(0); }}>
          <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualquer qualidade</SelectItem>
            <SelectItem value="baixa">Baixa</SelectItem>
            <SelectItem value="media">Média</SelectItem>
            <SelectItem value="alta">Alta</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => { setSortBy(v as SortOption); setPage(0); }}>
          <SelectTrigger className="w-52 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Mais recentes</SelectItem>
            <SelectItem value="oldest">Mais antigas</SelectItem>
            <SelectItem value="crawler_recent">Recentes do Robô</SelectItem>
            <SelectItem value="quality_asc">Menor qualidade</SelectItem>
            <SelectItem value="quality_desc">Maior qualidade</SelectItem>
          </SelectContent>
        </Select>
        <Select value={stateFilter ?? "all"} onValueChange={handleStateChange}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estados</SelectItem>
            {availableStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <CityCombobox cities={availableCities} value={cityFilter} onChange={handleCityChange} />
        {(orgFilter !== "all" || qualityFilter !== "all" || sortBy !== "recent" || searchTerm || stateFilter || cityFilter) && (
          <button
            onClick={() => { setOrgFilter("all"); setQualityFilter("all"); setSortBy("recent"); setSearchTerm(""); setStateFilter(null); setCityFilter(null); setPage(0); }}
            className="text-xs text-destructive hover:underline ml-auto"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Batch action bar */}
      {someSelected && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl p-3"
        >
          <span className="text-sm font-medium text-foreground">{selected.size} selecionada(s)</span>
          <Button
            size="sm"
            onClick={() => batchUpdate.mutate({ ids: [...selected], status: "publicada" })}
            disabled={batchUpdate.isPending}
          >
            {batchUpdate.isPending && batchUpdate.variables?.status === "publicada" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Publicar selecionadas
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setConfirmDiscard({ type: "batch", ids: [...selected], status: "descartada" })}
            disabled={batchUpdate.isPending}
          >
            {batchUpdate.isPending && batchUpdate.variables?.status === "descartada" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />} Descartar selecionadas
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => batchUpdate.mutate({ ids: [...selected], status: "pending_review" })}
            disabled={batchUpdate.isPending}
          >
            {batchUpdate.isPending && batchUpdate.variables?.status === "pending_review" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bot className="w-4 h-4 mr-1" />} Devolver p/ enriquecimento
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Limpar
          </Button>
        </motion.div>
      )}

      {/* Selection controls + page size */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Checkbox checked={allPageSelected} onCheckedChange={() => selectAllPage(pageIds)} />
        <span className="text-xs text-muted-foreground">Selecionar página ({filteredJobs.length})</span>
        <button
          onClick={selectAllFiltered}
          className="text-xs text-primary hover:underline"
        >
          {selected.size >= totalCount && totalCount > 0 ? "Desmarcar todas" : `Selecionar todas (${Math.min(totalCount, 500)}) · máx. 500`}
        </button>
        <div className="flex-1" />
        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
          <SelectTrigger className="h-8 w-[100px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>{size} / pág</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Review section */}
      {reviewJobs.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-4">
          <h2 className="font-heading text-base font-semibold text-foreground mb-3">Aguardando revisão</h2>
          <div className="space-y-3">
            {reviewJobs.map((job) => {
              const meta = (job as any).crawler_metadata?.[0];
              const isCrawlerJob = isCrawler(job);
              const isSelected = selected.has(job.id);
              return (
                <div
                  key={job.id}
                  className={`bg-card rounded-2xl p-4 border transition-colors ${
                    isSelected ? "border-primary bg-primary/5" : "border-amber-200 dark:border-amber-800"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="pt-1">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(job.id)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-foreground text-sm">{job.title}</h3>
                        {isCrawlerJob && (
                          <Badge variant="secondary" className="text-[10px] flex items-center gap-1">
                            <Bot className="w-3 h-3" /> Crawler
                          </Badge>
                        )}
                        <JobQualityBadge job={job} />
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap text-xs text-muted-foreground">
                        <span>{getCompanyName(job)} · {job.employment_type}</span>
                        <WorkModelBadge model={job.work_model} size="sm" />
                        {job.location_city && <span>· {job.location_city}</span>}
                        <DeadlineBadge deadline={job.deadline_date} size="sm" />
                        {isCrawlerJob && meta?.source_name && (
                          <SourceBadge sourceName={meta.source_name} sourceType={meta.source_type} size="sm" />
                        )}
                        {isSearching && (job as any)._match_rank && (
                          <span className="ml-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium">
                            {MATCH_RANK_LABELS[(job as any)._match_rank] ?? "match"}
                          </span>
                        )}
                      </div>
                      <CompletenessBar job={job} />
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0 items-center">
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setSelectedJob(job)} title="Ver detalhes">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => navigate(`/admin/vagas/${job.id}/revisar`)} title="Revisar vaga">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {meta?.source_url && (
                        <Button size="sm" variant="ghost" className="h-8 px-2" asChild title="Ver original">
                          <a href={meta.source_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="h-8"
                        onClick={() => updateStatus.mutate({ id: job.id, status: "publicada" })}
                        disabled={updateStatus.isPending && updateStatus.variables?.id === job.id}
                      >
                        {updateStatus.isPending && updateStatus.variables?.id === job.id && updateStatus.variables?.status === "publicada"
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Check className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-8"
                        onClick={() => setConfirmDiscard({ type: "single", id: job.id, status: isCrawlerJob ? "descartada" : "rascunho" })}
                        disabled={updateStatus.isPending && updateStatus.variables?.id === job.id}
                      >
                        {updateStatus.isPending && updateStatus.variables?.id === job.id && (updateStatus.variables?.status === "descartada" || updateStatus.variables?.status === "rascunho")
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <X className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.section>
      )}

      {reviewJobs.length === 0 && (
        <div className="mt-6 bg-muted/50 rounded-2xl p-8 text-center">
          <Check className="w-10 h-10 text-green-500 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Nenhuma vaga aguardando revisão</p>
        </div>
      )}

      {/* No "all jobs" section - this page only shows em_revisao */}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-xs text-muted-foreground">
              Página {page + 1} de {totalPages} · {totalCount} vaga(s)
            </p>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
              <SelectTrigger className="h-7 w-[85px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>{size} / pág</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const startPage = Math.max(0, Math.min(page - 2, totalPages - 5));
              const p = startPage + i;
              return (
                <Button
                  key={p}
                  size="sm"
                  variant={p === page ? "default" : "ghost"}
                  className="h-7 w-7 px-0 text-xs"
                  onClick={() => setPage(p)}
                >
                  {p + 1}
                </Button>
              );
            })}
            <Button size="sm" variant="ghost" className="h-7 px-2" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Job Detail Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={(open) => !open && setSelectedJob(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedJob && (() => {
            const meta = (selectedJob as any).crawler_metadata?.[0];
            const isCrawlerJob = isCrawler(selectedJob);
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 flex-wrap">
                    <DialogTitle className="text-xl">{selectedJob.title}</DialogTitle>
                    {isCrawlerJob && (
                      <Badge variant="secondary" className="text-xs flex items-center gap-1">
                        <Bot className="w-3 h-3" /> Crawler
                      </Badge>
                    )}
                    <JobQualityBadge job={selectedJob} />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                    <span>{getCompanyName(selectedJob)} · {selectedJob.employment_type}</span>
                    <WorkModelBadge model={selectedJob.work_model} size="sm" />
                    <DeadlineBadge deadline={selectedJob.deadline_date} size="sm" />
                  </div>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                  {meta && (
                    <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-2"><Bot className="w-4 h-4" /> Origem</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Fonte:</span> <SourceBadge sourceName={meta.source_name} sourceType={meta.source_type} size="sm" /></div>
                        {meta.company_name && <div><span className="text-muted-foreground">Empresa original:</span> <span className="font-medium">{meta.company_name}</span></div>}
                        {meta.crawler_score != null && <div><span className="text-muted-foreground">Score:</span> <span className="font-medium">{meta.crawler_score}</span></div>}
                      </div>
                      <div className="flex gap-3 text-xs">
                        {meta.source_url && (
                          <a href={meta.source_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> Link original
                          </a>
                        )}
                        {meta.external_apply_url && (
                          <a href={meta.external_apply_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> Link candidatura
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Status:</span>
                    <Badge variant={statusLabels[selectedJob.status]?.variant ?? "outline"}>
                      {statusLabels[selectedJob.status]?.label ?? selectedJob.status}
                    </Badge>
                  </div>

                  {selectedJob.location_city && (
                    <div>
                      <span className="text-sm font-medium text-foreground">Local: </span>
                      <span className="text-sm text-muted-foreground">{selectedJob.location_city}{selectedJob.location_state ? ` - ${selectedJob.location_state}` : ""}</span>
                    </div>
                  )}

                  {selectedJob.description && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">Descrição</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJob.description}</p>
                    </div>
                  )}

                  {Array.isArray(selectedJob.responsibilities) && selectedJob.responsibilities.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">Responsabilidades</h4>
                      <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                        {(selectedJob.responsibilities as string[]).map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  )}

                  {Array.isArray(selectedJob.requirements_must) && selectedJob.requirements_must.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">Requisitos</h4>
                      <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                        {(selectedJob.requirements_must as string[]).map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  )}

                  {Array.isArray(selectedJob.required_skills) && selectedJob.required_skills.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">Habilidades Obrigatórias</h4>
                      <SkillBadgeList skills={selectedJob.required_skills} />
                    </div>
                  )}

                  {Array.isArray(selectedJob.nice_skills) && selectedJob.nice_skills.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">Habilidades Desejáveis</h4>
                      <SkillBadgeList skills={selectedJob.nice_skills} />
                    </div>
                  )}

                  {selectedJob.deadline_date && (
                    <div>
                      <span className="text-sm font-medium text-foreground">Prazo: </span>
                      <span className="text-sm text-muted-foreground">{new Date(selectedJob.deadline_date).toLocaleDateString("pt-BR")}</span>
                    </div>
                  )}

                  <div className="flex gap-2 pt-4 border-t border-border flex-wrap">
                    <Button variant="outline" onClick={() => { setSelectedJob(null); navigate(`/admin/vagas/${selectedJob.id}/revisar`); }}>
                      <Pencil className="w-4 h-4 mr-1" /> Revisar vaga
                    </Button>
                    {selectedJob.status !== "publicada" && (
                      <Button
                        variant="hero"
                        onClick={() => updateStatus.mutate({ id: selectedJob.id, status: "publicada" })}
                        disabled={updateStatus.isPending}
                      >
                        {updateStatus.isPending && updateStatus.variables?.status === "publicada"
                          ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          : <Check className="w-4 h-4 mr-1" />} Publicar
                      </Button>
                    )}
                    {selectedJob.status === "publicada" && (
                      <Button
                        variant="outline"
                        onClick={() => updateStatus.mutate({ id: selectedJob.id, status: "pausada" })}
                        disabled={updateStatus.isPending}
                      >
                        {updateStatus.isPending && updateStatus.variables?.status === "pausada"
                          ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          : null} Pausar
                      </Button>
                    )}
                    {selectedJob.status === "em_revisao" && (
                      isCrawlerJob ? (
                        <Button
                          variant="destructive"
                          onClick={() => setConfirmDiscard({ type: "single", id: selectedJob.id, status: "descartada" })}
                          disabled={updateStatus.isPending}
                        >
                          {updateStatus.isPending && updateStatus.variables?.status === "descartada"
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <X className="w-4 h-4 mr-1" />} Descartar
                        </Button>
                      ) : (
                        <Button
                          variant="destructive"
                          onClick={() => setConfirmDiscard({ type: "single", id: selectedJob.id, status: "rascunho" })}
                          disabled={updateStatus.isPending}
                        >
                          {updateStatus.isPending && updateStatus.variables?.status === "rascunho"
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <X className="w-4 h-4 mr-1" />} Devolver
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Confirm discard/devolver dialog */}
      <AlertDialog open={!!confirmDiscard} onOpenChange={(open) => !open && setConfirmDiscard(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDiscard?.status === "descartada"
                ? `Descartar ${confirmDiscard?.type === "batch" ? `${confirmDiscard.ids.length} vaga(s)` : "vaga"}?`
                : `Devolver ${confirmDiscard?.type === "batch" ? `${(confirmDiscard as any).ids.length} vaga(s)` : "vaga"}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDiscard?.status === "descartada"
                ? confirmDiscard?.type === "batch"
                  ? `Tem certeza que deseja descartar ${confirmDiscard.ids.length} vaga(s) selecionada(s)?`
                  : "Tem certeza que deseja descartar esta vaga?"
                : confirmDiscard?.type === "batch"
                  ? `Tem certeza que deseja devolver ${(confirmDiscard as any).ids.length} vaga(s) para rascunho?`
                  : "Tem certeza que deseja devolver esta vaga para rascunho?"}
              {" "}Essa ação pode ser revertida alterando o status depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!confirmDiscard) return;
                if (confirmDiscard.type === "batch") {
                  batchUpdate.mutate({ ids: confirmDiscard.ids, status: confirmDiscard.status });
                } else {
                  updateStatus.mutate({ id: confirmDiscard.id, status: confirmDiscard.status });
                }
                setConfirmDiscard(null);
              }}
            >
              {confirmDiscard?.status === "descartada" ? "Descartar" : "Devolver"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
